import "./TagifyJobs.css";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useHub } from "../../core/Global";
import Icon from "../base/Icon";
import Indicator from "../base/Indicator";
import PhoneItem from "../../models/PhoneItem";

/**
 * TagifyAccounts — a native customer-account picker, built like TagifyJobs (portal dropdown,
 * keyboard navigation, no @yaireo/tagify).
 *
 * - Searches the server as the user types; nothing is listed before two characters.
 * - Single select. The chosen account sits in the control as a pill with a clear button.
 * - The browser's own autofill is switched off: it covered the suggestions completely.
 */

export interface AccountSuggestion {
    id: string;
    name?: string;
    username?: string;
    email?: string;
    phone?: string;
    phoneItem?: PhoneItem;
    avatar?: string;
    company?: string;
    plan?: string;
    suspended?: boolean;
}

interface IProps {
    id: string;
    /** Searched with ?q=keyword; one account is read from apiUrl + "/" + id. */
    apiUrl: string;
    placeholder?: string;
    selectedId?: string;
    onSelect?: (account?: AccountSuggestion) => void;
}

const initials = (s: AccountSuggestion) =>
    (s.name ?? s.username ?? "?").split(" ").map(x => x[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

export default function TagifyAccounts(props: IProps) {

    const hub = useHub();

    const controlRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<any>(null);
    const selectingRef = useRef<boolean>(false);

    const [query, setQuery] = useState<string>("");
    const [open, setOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [suggestions, setSuggestions] = useState<AccountSuggestion[]>([]);
    const [selected, setSelected] = useState<AccountSuggestion>();
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const [rect, setRect] = useState<{ top: number; left: number; width: number }>();

    // ----- preselect -----
    useEffect(() => {
        if (!props.selectedId) { setSelected(undefined); return; }
        if (selected?.id === props.selectedId) return;
        hub.Get(props.apiUrl + "/" + props.selectedId, res => {
            if (res?.success && res.data) setSelected(res.data);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.selectedId]);

    // ----- dropdown positioning (portal, fixed) -----
    const updateRect = () => {
        const el = controlRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setRect({ top: r.bottom + 4, left: r.left, width: r.width });
    };

    useEffect(() => {
        if (!open) return;
        updateRect();
        const handler = () => updateRect();
        window.addEventListener("scroll", handler, true);
        window.addEventListener("resize", handler);
        return () => {
            window.removeEventListener("scroll", handler, true);
            window.removeEventListener("resize", handler);
        };
    }, [open]);

    // ----- outside click -----
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (selectingRef.current) { selectingRef.current = false; return; }
            const t = e.target as Node;
            if (controlRef.current?.contains(t)) return;
            if (dropdownRef.current?.contains(t)) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    // ----- search -----
    const runSearch = (keyword: string) => {
        clearTimeout(debounceRef.current);
        if (keyword.trim().length < 2) {
            setLoading(false);
            setSuggestions([]);
            return;
        }
        setLoading(true);
        debounceRef.current = setTimeout(() => {
            hub.Get(props.apiUrl + "?q=" + encodeURIComponent(keyword.trim()), res => {
                setLoading(false);
                setSuggestions(res?.data ?? []);
                setActiveIndex(-1);
            });
        }, 300);
    };

    const select = (s: AccountSuggestion) => {
        setSelected(s);
        setQuery("");
        setSuggestions([]);
        setOpen(false);
        props.onSelect?.(s);
    };

    const clear = () => {
        setSelected(undefined);
        props.onSelect?.(undefined);
        setTimeout(() => { inputRef.current?.focus(); setOpen(true); }, 0);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex(i => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (activeIndex >= 0 && suggestions[activeIndex]) select(suggestions[activeIndex]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    useEffect(() => {
        if (activeIndex < 0 || !dropdownRef.current) return;
        const el = dropdownRef.current.querySelectorAll(".tj-suggestion")[activeIndex] as HTMLElement;
        el?.scrollIntoView({ block: "nearest" });
    }, [activeIndex]);

    // ----- render -----

    const Avatar = (s: AccountSuggestion, size: number) =>
        s.avatar
            ? <img src={s.avatar} alt="" className="rounded-circle flex-shrink-0" style={{ width: size, height: size, objectFit: "cover" }}
                onError={e => { (e.target as HTMLImageElement).style.visibility = "hidden"; }} />
            : <span className="symbol-label bg-light-primary text-primary fw-bold rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: size, height: size, fontSize: size * 0.38 }}>{initials(s)}</span>;

    const Row = (s: AccountSuggestion, i: number) => (
        <div key={s.id}
            className={"tj-suggestion d-flex flex-row align-items-center" + (i === activeIndex ? " tj-active" : "")}
            onMouseEnter={() => setActiveIndex(i)}
            onMouseDown={e => {
                e.preventDefault();
                selectingRef.current = true;
                select(s);
                setTimeout(() => { selectingRef.current = false; }, 0);
            }}>
            {Avatar(s, 34)}
            <div className="min-w-0 flex-grow-1 ms-3">
                <div className="d-flex align-items-center">
                    <span className="fw-bold text-gray-900 text-truncate">{s.name}</span>
                    <span className="text-gray-500 fs-8 ms-2 flex-shrink-0">@{s.username}</span>
                </div>
                <div className="d-flex align-items-center flex-wrap fs-8 text-gray-600 mt-1">
                    {s.company && <span className="d-flex align-items-center me-3"><Icon icon="building" size={10} className="me-1 text-gray-400" />{s.company}</span>}
                    {s.email && <span className="d-flex align-items-center me-3 text-truncate"><Icon icon="envelope" size={10} className="me-1 text-gray-400" />{s.email}</span>}
                </div>
            </div>
            <div className="d-flex flex-column align-items-end ms-3 flex-shrink-0">
                {s.plan && <span className="badge badge-light-primary fs-9">{s.plan}</span>}
                {s.suspended && <span className="badge badge-light-danger fs-9 mt-1">Suspended</span>}
            </div>
        </div>
    );

    return (
        <>
            <div ref={controlRef}
                className="form-control d-flex align-items-center tj-control"
                onClick={() => { if (!selected) { inputRef.current?.focus(); setOpen(true); } }}>
                {
                    selected ?
                        <div className="tj-pill d-flex align-items-center w-100">
                            {Avatar(selected, 22)}
                            <span className="text-truncate ms-2">
                                <span className="fw-bold">{selected.name}</span>
                                <span className="text-gray-500 ms-2">@{selected.username}</span>
                                {selected.plan && <span className="badge badge-light-primary fs-9 ms-2">{selected.plan}</span>}
                            </span>
                            <span className="tj-clear ms-auto ps-2" title="Choose another account"
                                onClick={e => { e.stopPropagation(); clear(); }}>
                                <Icon icon="xmark" />
                            </span>
                        </div>
                        :
                        <>
                            <Icon icon="magnifying-glass" size={12} className="text-gray-500 me-2" />
                            <input ref={inputRef} id={props.id} type="text"
                                // "off" alone is ignored for anything that looks like an email field;
                                // an unknown value makes the browser leave it alone.
                                autoComplete="rinvox-account-search" name={props.id + "-search"}
                                spellCheck={false}
                                className="tj-input border-0 p-0 flex-grow-1"
                                style={{ outline: "none", background: "transparent" }}
                                placeholder={props.placeholder ?? "Search name, username or email"}
                                value={query}
                                onFocus={() => setOpen(true)}
                                onChange={e => { setQuery(e.target.value); setOpen(true); runSearch(e.target.value); }}
                                onKeyDown={onKeyDown} />
                        </>
                }
            </div>

            {
                open && !selected && rect && query.trim().length >= 2 &&
                createPortal(
                    <div ref={dropdownRef} className="tj-dropdown"
                        style={{ top: rect.top, left: rect.left, width: rect.width }}>
                        {
                            loading ?
                                <div className="py-6"><Indicator noText /></div>
                                : suggestions.length > 0 ?
                                    suggestions.map((s, i) => Row(s, i))
                                    :
                                    <div className="tj-empty">No customer accounts found</div>
                        }
                    </div>,
                    document.body
                )
            }
        </>
    );
}
